import { useEffect, useRef, useState } from 'react';

interface RevealOptions {
  threshold?: number;
  rootMargin?: string;
  once?: boolean;
}

/**
 * useReveal — IntersectionObserver-based scroll reveal hook.
 * Respects prefers-reduced-motion: returns visible=true immediately.
 *
 * Usage:
 *   const { ref, isVisible } = useReveal();
 *   <div ref={ref} className={isVisible ? 'revealed' : 'hidden'} />
 */
export function useReveal<T extends HTMLElement = HTMLDivElement>(
  options: RevealOptions = {}
) {
  const { threshold = 0.15, rootMargin = '0px 0px -80px 0px', once = true } = options;
  const ref = useRef<T>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    // Respect reduced motion — show immediately
    const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReduced) {
      setIsVisible(true);
      return;
    }

    const node = ref.current;
    if (!node) return;

    // Fallback for very old browsers
    if (!('IntersectionObserver' in window)) {
      setIsVisible(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          if (once) observer.unobserve(node);
        } else if (!once) {
          setIsVisible(false);
        }
      },
      { threshold, rootMargin }
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [threshold, rootMargin, once]);

  return { ref, isVisible };
}

/**
 * useFocusTrap — trap focus within a container (for modals, drawers).
 *
 * Usage:
 *   const { ref, activate, deactivate } = useFocusTrap();
 *   // activate on open, deactivate on close
 */
export function useFocusTrap<T extends HTMLElement = HTMLDivElement>() {
  const ref = useRef<T>(null);

  const activate = () => {
    const node = ref.current;
    if (!node) return;

    const focusable = node.querySelectorAll<HTMLElement>(
      'a[href], button:not([disabled]), input:not([disabled]), textarea:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
    );
    if (focusable.length > 0) focusable[0].focus();

    const handleKey = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return;
      if (focusable.length === 0) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (e.shiftKey) {
        if (document.activeElement === first) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };

    node.addEventListener('keydown', handleKey);
    (node as any)._focusTrapHandler = handleKey;
  };

  const deactivate = () => {
    const node = ref.current;
    if (!node) return;
    const handler = (node as any)._focusTrapHandler;
    if (handler) {
      node.removeEventListener('keydown', handler);
      delete (node as any)._focusTrapHandler;
    }
  };

  return { ref, activate, deactivate };
}
